import { toObservable } from '@angular/core/rxjs-interop';
import { DestroyRef, Directive, ElementRef, inject, output, signal } from '@angular/core';
import { filter } from 'rxjs/operators';

const LONG_PRESS_TIMEOUT = 1000;

/**
 * Directive that indicates if the user held the mouse/touch down for a while before
 * releasing and triggering a click event. The longPress output will always be raised
 * right away with false. If the user holds it down long enough then another longPress
 * output will be raised with true. The last raised value should be remembered and then
 * the click event can choose what to do based on that value.
 * 
 * ✨ Fully modernized implementation using Angular signals and native DOM events.
 * Uses toObservable() for targeted reactivity instead of effect() for better performance.
 */
@Directive({
	selector: '[longPress]',
	standalone: true
})
export class LongPressDirective {
	static instances = 0;
	instanceId: number;

	longPress = output<boolean>();

	private elementRef = inject(ElementRef);
	private destroyRef = inject(DestroyRef);

	// Signal to track the current long press state
	private longPressState = signal<boolean | undefined>(undefined);

	// Signal to track if a press is currently active
	private isPressing = signal(false);

	// Store timeout ID for cleanup
	private timeoutId: number | null = null;

	constructor() {
		this.instanceId = ++LongPressDirective.instances;

		// Setup native event listeners
		this.setupNativeEventListeners();

		toObservable(this.longPressState)
			.pipe(
				filter((value): value is boolean => value !== undefined)
			)
			.subscribe(value => {
				this.longPress.emit(value);
			});
	}

	private setupNativeEventListeners(): void {
		const element = this.elementRef.nativeElement as HTMLElement;

		// Mouse event handlers
		const handleMouseDown = (event: MouseEvent) => {
			// Only handle primary (left) mouse button
			if (event.button === 0) {
				this.handlePressStart();
			}
		};

		const handleMouseUp = (event: MouseEvent) => {
			if (event.button === 0) {
				this.handlePressEnd();
			}
		};

		// Touch event handlers
		const handleTouchStart = () => {
			this.handlePressStart();
		};

		const handleTouchEnd = () => {
			this.handlePressEnd();
		};

		// Add event listeners to element
		element.addEventListener('mousedown', handleMouseDown);
		element.addEventListener('touchstart', handleTouchStart);

		// Add global listeners for mouse/touch end (in case user moves outside element)
		window.addEventListener('mouseup', handleMouseUp);
		element.addEventListener('touchend', handleTouchEnd);

		// Cleanup on destroy
		this.destroyRef.onDestroy(() => {
			console.log(`LongPressDirective #${this.instanceId} destroyed`);

			// Remove all event listeners
			element.removeEventListener('mousedown', handleMouseDown);
			element.removeEventListener('touchstart', handleTouchStart);
			window.removeEventListener('mouseup', handleMouseUp);
			element.removeEventListener('touchend', handleTouchEnd);

			// Clear any pending timeout
			if (this.timeoutId !== null) {
				window.clearTimeout(this.timeoutId);
			}
		});
	}

	private handlePressStart(): void {
		if (this.isPressing()) return; // Already pressing

		this.isPressing.set(true);

		// Immediately emit false when press starts
		this.longPressState.set(false);

		// Start timer for long press
		this.timeoutId = window.setTimeout(() => {
			if (this.isPressing()) {
				// User held long enough - emit true
				this.longPressState.set(true);
			}
		}, LONG_PRESS_TIMEOUT);
	}

	private handlePressEnd(): void {
		if (!this.isPressing()) return; // Not pressing

		// Clear the long press timeout
		if (this.timeoutId !== null) {
			window.clearTimeout(this.timeoutId);
			this.timeoutId = null;
		}

		// Small delay before emitting false to allow click event to check the value
		setTimeout(() => {
			this.longPressState.set(false);
			this.isPressing.set(false);
		}, 100);
	}
}
